import React, { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Bits";
import { Sparkles, Send, Plus, MessageSquare, Trash2, Rocket, Infinity as InfIcon } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Where am I leaking money?",
  "What's my biggest behavioral mistake?",
  "Which setup is my real edge?",
  "Am I overtrading?",
];

const greeting = { role: "assistant", content: "Coach online. I've ingested your full trade log, time-of-day breakdowns, opening drives, and recent behavioral signals from your recaps. Ask me anything — I'll give it to you straight." };

export default function Coach() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([greeting]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  const { data: sessions = [] } = useQuery({
    queryKey: ["coach-sessions"],
    queryFn: async () => (await api.get("/coach/sessions")).data,
  });

  const { data: usage } = useQuery({
    queryKey: ["ai-usage"],
    queryFn: async () => (await api.get("/ai/usage")).data,
    refetchInterval: 30000,
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Auto-send prompt from query string (e.g. ?prompt=Coach+me+on+...)
  /* eslint-disable */
  useEffect(() => {
    const p = params.get("prompt");
    if (p && !streaming) {
      setParams({}, { replace: true });
      // small delay so the greeting renders first
      setTimeout(() => send(p), 100);
    }
  }, []);
  /* eslint-enable */

  const loadSession = async (sid) => {
    setSessionId(sid);
    try {
      const r = await api.get(`/coach/sessions/${sid}/messages`);
      setMessages(r.data.length ? r.data : [greeting]);
    } catch { setMessages([greeting]); }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([greeting]);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/coach/sessions/${sid}`);
      qc.invalidateQueries({ queryKey: ["coach-sessions"] });
      if (sessionId === sid) newChat();
    } catch { toast.error("Failed to delete"); }
  };

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("edge_token");
      const res = await fetch(`${API_BASE}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question, session_id: sessionId }),
      });
      if (!res.ok || !res.body) {
        if (res.status === 429) {
          try {
            const err = await res.json();
            const detail = err?.detail || err;
            toast.error(detail?.message || "Daily AI limit reached — upgrade to Pro for unlimited.");
            setMessages((m) => m.slice(0, -1));  // remove blank assistant reply
            qc.invalidateQueries({ queryKey: ["ai-usage"] });
            setStreaming(false);
            return;
          } catch { /* ignore */ }
        }
        throw new Error(`HTTP ${res.status}`);
      }

      // Capture session id from response header
      const newSid = res.headers.get("X-Session-Id");
      if (newSid && newSid !== sessionId) setSessionId(newSid);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
      qc.invalidateQueries({ queryKey: ["coach-sessions"] });
      qc.invalidateQueries({ queryKey: ["ai-usage"] });
    } catch {
      toast.error("Coach unavailable. Try again.");
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "[error: coach unreachable]" };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <PageHeader
        eyebrow="COACH / CLAUDE SONNET 4.5"
        title="Your Performance Coach"
        action={
          <div className="flex items-center gap-3">
            <button onClick={newChat} data-testid="new-chat-btn" className="flex items-center gap-2 px-3 py-2 text-xs bg-[color:var(--surface)] hover:bg-[color:var(--surface2)] border border-[color:var(--border)] transition-colors">
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5}/> NEW CHAT
            </button>
            {usage && (
              usage.unlimited ? (
                <div className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono-num" style={{ background: "rgba(168,85,247,0.12)", color: "var(--accent)", borderRadius: "8px", border: "1px solid rgba(168,85,247,0.35)" }} data-testid="ai-usage-pill">
                  <InfIcon className="h-3.5 w-3.5" strokeWidth={1.5} /> UNLIMITED · PRO
                </div>
              ) : (
                <div className="flex items-center gap-2" data-testid="ai-usage-pill">
                  <div className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono-num" style={{
                    background: usage.used >= usage.limit ? "rgba(244,63,94,0.12)" : "var(--surface)",
                    color: usage.used >= usage.limit ? "var(--loss)" : "var(--fg-2)",
                    borderRadius: "8px",
                    border: `1px solid ${usage.used >= usage.limit ? "rgba(244,63,94,0.35)" : "var(--border)"}`,
                  }}>
                    {usage.used}/{usage.limit} MSG TODAY
                  </div>
                  <button
                    onClick={() => navigate("/pricing")}
                    data-testid="upgrade-mini-cta"
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-all"
                    style={{ background: "var(--accent)", color: "white", borderRadius: "8px" }}
                    title="Upgrade to Pro for unlimited"
                  >
                    <Rocket className="h-3 w-3" strokeWidth={2} /> GO PRO
                  </button>
                </div>
              )
            )}
            <div className="flex items-center gap-2 text-xs text-[color:var(--accent)]">
              <span className="w-1.5 h-1.5 bg-[color:var(--accent)] rounded-full animate-pulse" /> ONLINE
            </div>
          </div>
        }
      />

      <div className="flex-1 grid grid-cols-[240px_1fr] min-h-0">
        {/* Sessions sidebar */}
        <aside className="border-r border-[color:var(--border)] overflow-y-auto">
          <div className="label-eyebrow px-4 py-3 border-b border-[color:var(--border)]">CONVERSATIONS</div>
          {sessions.length === 0 ? (
            <div className="p-4 text-xs text-[color:var(--fg-3)]">No saved chats yet.</div>
          ) : sessions.map((s) => (
            <div key={s.id} onClick={() => loadSession(s.id)}
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") loadSession(s.id); }}
              data-testid={`session-${s.id}`}
              className={`w-full text-left p-3 border-b border-[color:var(--border)] hover:bg-[color:var(--surface)]/60 transition-colors group cursor-pointer ${sessionId === s.id ? "bg-[color:var(--surface)]/80 border-l-2 border-l-[color:var(--accent)]" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <MessageSquare className="h-3 w-3 mt-0.5 text-[color:var(--fg-3)] shrink-0" strokeWidth={1.5} />
                  <div className="text-xs text-[color:var(--fg)] truncate">{s.title}</div>
                </div>
                <button onClick={(e) => deleteSession(s.id, e)} className="opacity-0 group-hover:opacity-100 text-[color:var(--fg-3)] hover:text-rose-400 transition-opacity" data-testid={`delete-session-${s.id}`}>
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </div>
              <div className="label-eyebrow mt-1">{s.updated_at?.slice(0, 10)}</div>
            </div>
          ))}
        </aside>

        {/* Chat */}
        <div className="flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`${m.role === "user" ? "ml-auto bg-[color:var(--surface)] border border-[color:var(--border)]" : "border border-[color:var(--accent)]/30 bg-[rgba(168,85,247,0.05)] relative"} max-w-[85%] p-4`} data-testid={`msg-${m.role}-${i}`}>
                  {m.role === "assistant" && (
                    <div className="flex items-center gap-2 mb-2 label-eyebrow text-[color:var(--accent)]">
                      <Sparkles className="h-3 w-3" strokeWidth={1.5} /> COACH
                    </div>
                  )}
                  {m.role === "user" && (
                    <div className="label-eyebrow mb-2 text-[color:var(--fg-3)]">OPERATOR</div>
                  )}
                  <div className={`text-sm leading-relaxed whitespace-pre-wrap ${m.role === "assistant" ? "font-mono-num text-[color:var(--fg)]" : "text-[color:var(--fg)]"}`}>
                    {m.content}
                    {streaming && i === messages.length - 1 && m.role === "assistant" && <span className="terminal-blink" />}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
          </div>

          <div className="border-t border-[color:var(--border)] px-8 py-4 bg-[color:var(--bg)]">
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-wrap gap-2 mb-3">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} disabled={streaming}
                    data-testid={`suggestion-${s}`}
                    className="text-xs px-2.5 py-1.5 border border-[color:var(--border)] text-[color:var(--fg-2)] hover:text-[color:var(--accent)] hover:border-[color:var(--accent)] transition-colors disabled:opacity-40">
                    {s}
                  </button>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
                <input
                  value={input} onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Coach…" disabled={streaming}
                  data-testid="coach-input"
                  className="flex-1 bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num"
                />
                <button type="submit" disabled={streaming || !input.trim()} data-testid="coach-send"
                  className="bg-[color:var(--accent)] text-black px-4 hover:bg-[color:var(--accent)] transition-colors disabled:opacity-40 flex items-center gap-2 text-sm">
                  <Send className="h-4 w-4" strokeWidth={1.5} /> SEND
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
